import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Select } from 'components/FormFields';
import Toaster from 'components/Toaster';
import DeleteReader from '../../AddReader/gql/DeleteReader';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AddReaderModal from './AddReaderModal';

// Section header with title and action button
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Settings card
const SettingsCard = styled.div`
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: 32px;
`;

const SettingsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToggleText = styled.div``;

const ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin-top: 2px;
`;

// Toggle switch
const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary},
      #ff6b3b
    );
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : theme.colors.card.borderHover};
  transition: all 0.3s ease;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: ${({ theme }) => theme.colors.white};
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

// Toggle with counter container
const ToggleWithCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SelectWrapper = styled.div`
  width: 200px;
`;

// Counter component
const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 4px;
`;

// Wrapper for counter buttons
const CounterButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

const CounterValue = styled.span`
  min-width: 40px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Readers list
const ReadersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReaderCard = styled.div`
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.card.borderHover};
    box-shadow: ${({ theme }) => theme.colors.card.shadowHover};
  }
`;

const ReaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ReaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReaderLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const ReaderDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const ReaderDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

const ReaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

// Wrapper for action buttons
const ActionButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

// Empty state
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ScannerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="6" y1="8" x2="6" y2="16" />
    <line x1="10" y1="8" x2="10" y2="16" />
    <line x1="14" y1="8" x2="14" y2="16" />
    <line x1="18" y1="8" x2="18" y2="16" />
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const HashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="9" x2="20" y2="9" />
    <line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" />
    <line x1="16" y1="3" x2="14" y2="21" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const LogOutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const KioskIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <rect x="8" y="6" width="8" height="6" rx="1" />
    <circle cx="12" cy="17" r="2" />
  </svg>
);

const PrinterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SeatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 18v-5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v5" />
    <path d="M4 18h16" />
    <path d="M6 18v2" />
    <path d="M18 18v2" />
    <path d="M8 10V7a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
  </svg>
);

const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

const ORPHANED_SEATS_OPTIONS = [
  { value: 'lenient', label: 'Lenient' },
  { value: 'strict', label: 'Strict' },
];

const HardwareTab = ({
  readers = [],
  janamScanners = false,
  janamScannerCount = 0,
  kioskScanners = false,
  kioskScannerCount = 0,
  posPrinters = false,
  posPrinterCount = 0,
  scanOut = false,
  orphanedSeats = null,
  bestAvailable = false,
  accountId,
  setFieldValue,
  deleteReader,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReader, setSelectedReader] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const handleDeleteClick = (reader) => {
    setSelectedReader(reader);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedReader) return;

    setIsDeleting(true);
    try {
      const response = await deleteReader(selectedReader.id);

      if (!response || response.errors) {
        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to delete reader. Please try again.',
        });
      } else {
        const newReaders = readers.filter(
          (reader) => reader.id !== selectedReader.id
        );
        setFieldValue('readers', newReaders);
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Success!',
          message: 'Reader deleted successfully.',
        });
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to delete reader. Please try again.',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedReader(null);
    }
  };

  const handleAddSuccess = (newReader) => {
    const newReaders = [...readers, newReader];
    setFieldValue('readers', newReaders);
    setShowAddModal(false);
    setToaster({
      isOpen: true,
      type: 'success',
      title: 'Success!',
      message: 'Reader added successfully.',
    });
  };

  const handleJanamToggle = (checked) => {
    setFieldValue('janam_scanners', checked);
    if (checked) {
      setFieldValue('janam_scanner_count', 1);
    } else {
      setFieldValue('janam_scanner_count', 0);
    }
  };

  const handleJanamCountChange = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    const newCount = Math.max(1, janamScannerCount + delta);
    setFieldValue('janam_scanner_count', newCount);
  };

  const handleKioskToggle = (checked) => {
    setFieldValue('kiosk_scanners', checked);
    if (checked) {
      setFieldValue('kiosk_scanner_count', 1);
    } else {
      setFieldValue('kiosk_scanner_count', 0);
    }
  };

  const handleKioskCountChange = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    const newCount = Math.max(1, kioskScannerCount + delta);
    setFieldValue('kiosk_scanner_count', newCount);
  };

  const handlePosPrinterToggle = (checked) => {
    setFieldValue('pos_printers', checked);
    if (checked) {
      setFieldValue('pos_printer_count', 1);
    } else {
      setFieldValue('pos_printer_count', 0);
    }
  };

  const handlePosPrinterCountChange = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    const newCount = Math.max(1, posPrinterCount + delta);
    setFieldValue('pos_printer_count', newCount);
  };

  const handleScanOutToggle = (checked) => {
    setFieldValue('scan_out', checked);
  };

  const handleOrphanedSeatsToggle = (checked) => {
    if (checked) {
      // Default to 'lenient' when enabling
      setFieldValue('orphaned_seats', 'lenient');
    } else {
      setFieldValue('orphaned_seats', null);
    }
  };

  const handleOrphanedSeatsModeChange = (e) => {
    setFieldValue('orphaned_seats', e.target.value);
  };

  const handleBestAvailableToggle = (checked) => {
    setFieldValue('best_available', checked);
  };

  return (
    <>
      {/* Device Settings */}
      <SettingsCard>
        <SettingsTitle>Device Settings</SettingsTitle>
        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <ScannerIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Janam Scanners</ToggleLabel>
              <ToggleDescription>
                Enable support for Janam handheld barcode scanners
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <ToggleWithCounter>
            {janamScanners && (
              <CounterContainer>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handleJanamCountChange(e, -1)}
                    disabled={janamScannerCount <= 1}
                    icon={<MinusIcon />}
                  />
                </CounterButtonWrapper>
                <CounterValue>{janamScannerCount}</CounterValue>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handleJanamCountChange(e, 1)}
                    icon={<PlusIcon />}
                  />
                </CounterButtonWrapper>
              </CounterContainer>
            )}
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={janamScanners}
                onChange={(e) => handleJanamToggle(e.target.checked)}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleWithCounter>
        </ToggleRow>

        <ToggleRow style={{ marginTop: '12px' }}>
          <ToggleInfo>
            <ToggleIcon>
              <KioskIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Kiosk Scanners</ToggleLabel>
              <ToggleDescription>
                Enable kiosk scanners for printing armbands
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <ToggleWithCounter>
            {kioskScanners && (
              <CounterContainer>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handleKioskCountChange(e, -1)}
                    disabled={kioskScannerCount <= 1}
                    icon={<MinusIcon />}
                  />
                </CounterButtonWrapper>
                <CounterValue>{kioskScannerCount}</CounterValue>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handleKioskCountChange(e, 1)}
                    icon={<PlusIcon />}
                  />
                </CounterButtonWrapper>
              </CounterContainer>
            )}
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={kioskScanners}
                onChange={(e) => handleKioskToggle(e.target.checked)}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleWithCounter>
        </ToggleRow>

        <ToggleRow style={{ marginTop: '12px' }}>
          <ToggleInfo>
            <ToggleIcon>
              <PrinterIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>POS Printers</ToggleLabel>
              <ToggleDescription>
                Enable point-of-sale receipt printers
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <ToggleWithCounter>
            {posPrinters && (
              <CounterContainer>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handlePosPrinterCountChange(e, -1)}
                    disabled={posPrinterCount <= 1}
                    icon={<MinusIcon />}
                  />
                </CounterButtonWrapper>
                <CounterValue>{posPrinterCount}</CounterValue>
                <CounterButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={(e) => handlePosPrinterCountChange(e, 1)}
                    icon={<PlusIcon />}
                  />
                </CounterButtonWrapper>
              </CounterContainer>
            )}
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={posPrinters}
                onChange={(e) => handlePosPrinterToggle(e.target.checked)}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleWithCounter>
        </ToggleRow>

        <ToggleRow style={{ marginTop: '12px' }}>
          <ToggleInfo>
            <ToggleIcon>
              <LogOutIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>
                Allow Scan {isTicketHoss ? 'Tickets' : 'Passes'} Out
              </ToggleLabel>
              <ToggleDescription>
                Enable scanning {isTicketHoss ? 'tickets' : 'passes'} for exit
                tracking at events
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={scanOut}
              onChange={(e) => handleScanOutToggle(e.target.checked)}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>
      </SettingsCard>

      {/* Reserved Seat Settings */}
      <SettingsCard>
        <SettingsTitle>Reserved Seat Settings</SettingsTitle>
        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <SeatIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Allow Orphaned Seats</ToggleLabel>
              <ToggleDescription>
                Control how single empty seats between purchases are handled
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <ToggleWithCounter>
            {orphanedSeats && (
              <SelectWrapper>
                <Select
                  value={orphanedSeats}
                  onChange={handleOrphanedSeatsModeChange}
                  options={ORPHANED_SEATS_OPTIONS}
                />
              </SelectWrapper>
            )}
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={!!orphanedSeats}
                onChange={(e) => handleOrphanedSeatsToggle(e.target.checked)}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleWithCounter>
        </ToggleRow>

        <ToggleRow style={{ marginTop: '12px' }}>
          <ToggleInfo>
            <ToggleIcon>
              <SeatIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Best Available</ToggleLabel>
              <ToggleDescription>
                Allow customers to select best available seats automatically
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={bestAvailable}
              onChange={(e) => handleBestAvailableToggle(e.target.checked)}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>
      </SettingsCard>

      {/* Card Readers Section */}
      <SectionHeader>
        <SectionTitle>
          Card Readers
          {readers.length > 0 && <ItemCount>({readers.length})</ItemCount>}
        </SectionTitle>
        <AddButtonWrapper>
          <ButtonV2
            elevated
            type="button"
            onClick={() => setShowAddModal(true)}
            icon={<PlusIcon />}
          >
            Add Reader
          </ButtonV2>
        </AddButtonWrapper>
      </SectionHeader>

      {readers.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>
              <CreditCardIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>No Card Readers</EmptyStateTitle>
            <EmptyStateText>
              Add card readers to enable in-person payments and ticket scanning
              at your events.
            </EmptyStateText>
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={() => setShowAddModal(true)}
                icon={<PlusIcon />}
              >
                Add Your First Reader
              </ButtonV2>
            </AddButtonWrapper>
          </EmptyState>
        </Card>
      ) : (
        <ReadersListContainer>
          {readers.map((reader) => (
            <ReaderCard key={reader.id}>
              <ReaderIcon>
                <CreditCardIcon />
              </ReaderIcon>
              <ReaderInfo>
                <ReaderLabel>{reader.label || 'Card Reader'}</ReaderLabel>
                <ReaderDetails>
                  {reader.reader_id && (
                    <ReaderDetail>
                      <HashIcon />
                      ID: {reader.reader_id}
                    </ReaderDetail>
                  )}
                  {reader.serial_number && (
                    <ReaderDetail>
                      <ScannerIcon />
                      S/N: {reader.serial_number}
                    </ReaderDetail>
                  )}
                </ReaderDetails>
              </ReaderInfo>
              <ReaderActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    variant="icon"
                    type="button"
                    onClick={() => handleDeleteClick(reader)}
                    title="Remove reader"
                    icon={<TrashIcon />}
                  />
                </ActionButtonWrapper>
              </ReaderActions>
            </ReaderCard>
          ))}
        </ReadersListContainer>
      )}

      <AddReaderModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        accountId={accountId}
        onSuccess={handleAddSuccess}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedReader(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={selectedReader?.label || 'this reader'}
        itemType="reader"
        isDeleting={isDeleting}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </>
  );
};

HardwareTab.propTypes = {
  readers: PropTypes.array,
  janamScanners: PropTypes.bool,
  janamScannerCount: PropTypes.number,
  kioskScanners: PropTypes.bool,
  kioskScannerCount: PropTypes.number,
  posPrinters: PropTypes.bool,
  posPrinterCount: PropTypes.number,
  scanOut: PropTypes.bool,
  orphanedSeats: PropTypes.string,
  bestAvailable: PropTypes.bool,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFieldValue: PropTypes.func.isRequired,
  deleteReader: PropTypes.func.isRequired,
};

export default compose(DeleteReader)(HardwareTab);
